/* Reading a construction drawing SET (a multi-page PDF) into a register: pull
   each page's text so the AI can name the sheet, and rasterise each page to a
   preview image. pdf.js is loaded on demand — it is heavy, and only this
   feature needs it. Ported from the Project Studio's proven pipeline. */

type PdfDoc = { numPages: number; getPage: (n: number) => Promise<any>; destroy: () => Promise<void> };

async function loadPdf(data: ArrayBuffer): Promise<PdfDoc> {
  const pdfjs = await import("pdfjs-dist");
  const worker = await import("pdfjs-dist/build/pdf.worker.min.mjs?url");
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  return (await pdfjs.getDocument({ data }).promise) as unknown as PdfDoc;
}

export async function pdfPageCount(file: File): Promise<number> {
  if (file.type !== "application/pdf" && !/\.pdf$/i.test(file.name)) return 0;
  const doc = await loadPdf(await file.arrayBuffer());
  try { return doc.numPages; } finally { try { await doc.destroy(); } catch { /* gone */ } }
}

/** Each page's text, reading-order reconstructed from item positions. Title
    blocks sit at the sheet edges, so we keep the head and tail of the order —
    enough for the AI to read the number, title and revision. */
export async function extractPdfPageTexts(file: File): Promise<string[]> {
  if (file.type !== "application/pdf" && !/\.pdf$/i.test(file.name)) return [];
  const doc = await loadPdf(await file.arrayBuffer());
  const out: string[] = [];
  try {
    for (let i = 1; i <= Math.min(doc.numPages, 400); i++) {
      const page = await doc.getPage(i);
      const content = await page.getTextContent();
      const byY = new Map<number, { x: number; str: string }[]>();
      for (const it of content.items as { str: string; transform: number[] }[]) {
        if (!it.str?.trim()) continue;
        const y = Math.round(it.transform[5] / 3) * 3;
        if (!byY.has(y)) byY.set(y, []);
        byY.get(y)!.push({ x: it.transform[4], str: it.str });
      }
      const lines: string[] = [];
      for (const [, items] of [...byY.entries()].sort((a, b) => b[0] - a[0])) {
        lines.push(items.sort((a, b) => a.x - b.x).map((x) => x.str).join(" ").replace(/\s+/g, " ").trim());
      }
      const head = lines.slice(0, 25).join("\n");
      const tail = lines.slice(-35).join("\n");
      out.push((head + "\n…\n" + tail).slice(0, 1800));
      try { page.cleanup?.(); } catch { /* gone */ }
    }
  } finally {
    try { await doc.destroy(); } catch { /* gone */ }
  }
  return out;
}

/** Rasterise one page (1-based) of a PDF — or read a plain image — to a JPEG
    data-URI. `hi` renders crisp (for the full-quality file); the default is
    a lighter thumbnail. Always tears the document down: pdf.js keeps every
    parsed page in memory, and a whole set back-to-back exhausts the tab. */
export async function renderSheetPreview(file: File, page = 1, hi = false, timeoutMs = 25_000): Promise<string | null> {
  if (/^image\//.test(file.type) || /\.(png|jpe?g|webp)$/i.test(file.name)) {
    try {
      const { fileToDataUri } = await import("./image");
      return await fileToDataUri(file, hi ? 2400 : 1600, hi ? 0.85 : 0.72);
    } catch { return null; }
  }
  if (file.type !== "application/pdf" && !/\.pdf$/i.test(file.name)) return null;
  let doc: PdfDoc | null = null;
  let pg: any = null;
  try {
    doc = await loadPdf(await file.arrayBuffer());
    pg = await doc.getPage(Math.min(Math.max(1, page), doc.numPages));
    const render = async (maxW: number, quality: number) => {
      const base = pg.getViewport({ scale: 1 });
      const viewport = pg.getViewport({ scale: maxW / base.width });
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(viewport.width); canvas.height = Math.round(viewport.height);
      const ctx = canvas.getContext("2d")!;
      ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, canvas.width, canvas.height);
      const task = pg.render({ canvasContext: ctx, viewport, intent: "print" });
      try {
        await Promise.race([
          task.promise,
          new Promise((_, rej) => setTimeout(() => { task.cancel(); rej(new Error("render timeout")); }, timeoutMs)),
        ]);
        return canvas.toDataURL("image/jpeg", quality);
      } finally { canvas.width = 0; canvas.height = 0; }
    };
    if (hi) return await render(2400, 0.85);
    // sharper thumbnails — line drawings compress well, so a bigger first pass
    // usually stays inside the store limit; fall back only when it doesn't
    let out = await render(1900, 0.78);
    if (out.length > 820_000) out = await render(1400, 0.66);
    if (out.length > 820_000) out = await render(1000, 0.55);
    return out.length > 850_000 ? null : out;
  } catch {
    return null;
  } finally {
    try { pg?.cleanup?.(); } catch { /* gone */ }
    try { await doc?.destroy(); } catch { /* gone */ }
  }
}

/** Render the ORIGINAL PDF at high resolution (up to 4200px, q0.92) for the
    full-screen viewer — far above preview quality so zooming stays crisp.
    `page` is the page inside a combined set. Returns null on any failure. */
export async function renderOriginalHi(data: ArrayBuffer, page?: number): Promise<string | null> {
  try {
    const doc = await loadPdf(data);
    try {
      const pg = await doc.getPage(Math.min(Math.max(1, page ?? 1), doc.numPages));
      const base = pg.getViewport({ scale: 1 });
      const scale = Math.min(4200 / base.width, 6);
      const viewport = pg.getViewport({ scale });
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(viewport.width);
      canvas.height = Math.round(viewport.height);
      const ctx = canvas.getContext("2d")!;
      ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, canvas.width, canvas.height);
      const task = pg.render({ canvasContext: ctx, viewport, intent: "print" });
      await Promise.race([
        task.promise,
        new Promise((_, rej) => setTimeout(() => { task.cancel(); rej(new Error("render timeout")); }, 30_000)),
      ]);
      const uri = canvas.toDataURL("image/jpeg", 0.92);
      canvas.width = 0; canvas.height = 0;
      try { pg.cleanup?.(); } catch { /* gone */ }
      return uri;
    } finally { try { await doc.destroy(); } catch { /* gone */ } }
  } catch { return null; }
}

export interface ScannedSheet { number: string; title: string; discipline: string; phase: string; revision: string | null }
export interface ScanPage { ref: string; text: string; image?: string }

/** Load an image (URL or data URI) and re-encode it downscaled for the vision
    scan — big enough to read a title block, small enough to keep tokens sane. */
export function imageForScan(src: string, maxW = 1500): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const s = Math.min(1, maxW / (img.naturalWidth || maxW));
        const w = Math.max(1, Math.round((img.naturalWidth || maxW) * s));
        const h = Math.max(1, Math.round((img.naturalHeight || maxW) * s));
        const c = document.createElement("canvas"); c.width = w; c.height = h;
        const ctx = c.getContext("2d"); if (!ctx) return resolve(null);
        ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, w, h); ctx.drawImage(img, 0, 0, w, h);
        resolve(c.toDataURL("image/jpeg", 0.82));
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/** One batch of pages → the worker. Returns aligned results (null per page on a
    batch failure, so indices never shift). */
async function scanBatch(pages: ScanPage[]): Promise<(ScannedSheet | null)[]> {
  try {
    const r = await fetch("/api/scan-drawings", {
      method: "POST", credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sheets: pages }),
    });
    if (!r.ok) return pages.map(() => null);
    const d = (await r.json()) as { sheets?: ScannedSheet[] };
    return Array.isArray(d.sheets) && d.sheets.length === pages.length ? d.sheets : pages.map(() => null);
  } catch { return pages.map(() => null); }
}

/** Name every page of the set. When pages carry a rendered `image` the worker
    reads the TITLE BLOCK visually (far more accurate) — those go in small batches
    (vision is token-heavy); text-only pages batch large. A failed batch yields
    nulls for its pages so a big set never fails wholesale. */
export async function scanDrawingSet(
  pages: ScanPage[],
  onProgress?: (done: number, total: number) => void,
): Promise<(ScannedSheet | null)[] | null> {
  const vision = pages.some((p) => p.image);
  const BATCH = vision ? 6 : 40;
  const CONC = vision ? 3 : 1;                 // run a few vision batches at once to cut wall-clock
  const chunks: ScanPage[][] = [];
  for (let i = 0; i < pages.length; i += BATCH) chunks.push(pages.slice(i, i + BATCH));
  const out: (ScannedSheet | null)[] = new Array(pages.length).fill(null);
  let done = 0;
  for (let g = 0; g < chunks.length; g += CONC) {
    const group = chunks.slice(g, g + CONC);
    const results = await Promise.all(group.map((c) => scanBatch(c)));
    results.forEach((res, gi) => {
      const start = (g + gi) * BATCH;
      res.forEach((r, ri) => { out[start + ri] = r; });
      done += res.length;
    });
    onProgress?.(Math.min(done, pages.length), pages.length);
  }
  return out.some(Boolean) ? out : null;
}
